import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Badge,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Card,
    CardContent,
    FormLabel,
    Radio,
    RadioGroup,
    FormControlLabel,
    Snackbar,
    SnackbarContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import MessageIcon from "@material-ui/icons/Message";
import NotificationsIcon from "@material-ui/icons/Notifications";
import ForumIcon from "@material-ui/icons/Forum";
import NoteService from "./Locality/Service/noteService";
import StudentService from "./Locality/Service/studentService";
import SocketService from "./Locality/Service/socketService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import PageTitle from "../../components/PageTitle/PageTitle";
import Widget from "../../components/Widget/Widget";
import { useFormik } from "formik";
import * as Yup from "yup";

const useStyles = makeStyles((theme) => ({
    yellowHeader: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px 24px',
        marginBottom: '24px',
        borderRadius: '4px 4px 0 0',
    },
    layout: {
        display: 'flex',
        gap: 16,
        minHeight: 600,
        alignItems: 'stretch',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    studentsPanel: {
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: 'min(600px, calc(100vh - 220px))',
        maxHeight: 'min(600px, calc(100vh - 220px))',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: 'min(400px, 50vh)',
            maxHeight: 'min(400px, 50vh)',
        },
    },
    studentsPanelHeader: {
        flexShrink: 0,
    },
    studentsListScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    notesPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    notesContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    noteBubble: {
        position: 'relative',
        padding: '14px 18px',
        borderRadius: 14,
        marginBottom: 12,
        backgroundColor: '#fffbe6',
        border: '1px solid #ffe38a',
        color: '#5b4407',
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
        '&:hover': {
            backgroundColor: '#fff3bf',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)',
        },
    },
    noteBubbleUnread: {
        backgroundColor: '#fff5d6',
        borderColor: '#ffc107',
        boxShadow: '0 1px 4px rgba(255,193,7,0.25)',
    },
    noteHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        flexWrap: 'wrap',
    },
    noteType: {
        fontWeight: 700,
        fontSize: '0.85rem',
        marginBottom: 6,
    },
    noteText: {
        fontSize: '0.9rem',
        marginBottom: 6,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    noteTime: {
        fontSize: '0.7rem',
        opacity: 0.75,
    },
    noteRowMeta: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 4,
    },
    unreadDot: {
        minWidth: 20,
        height: 20,
        padding: '0 6px',
        borderRadius: 10,
        backgroundColor: '#e53935',
        color: 'white',
        fontSize: '0.7rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    studentRow: {
        position: 'relative',
        paddingRight: 36,
    },
    studentRowName: {
        display: 'block',
        fontSize: '0.95rem',
    },
    studentRowMeta: {
        display: 'block',
        fontSize: '0.75rem',
        color: '#666',
        marginTop: 2,
    },
    studentRowDot: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        minWidth: 22,
        height: 22,
        padding: '0 7px',
        borderRadius: 11,
        backgroundColor: '#e53935',
        color: 'white',
        fontSize: '0.75rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chatDialog: {
        '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '90vw',
            height: '600px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    chatHeader: {
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    chatMessageBubble: {
        padding: '10px 16px',
        borderRadius: 18,
        marginBottom: 12,
        maxWidth: '70%',
        wordWrap: 'break-word',
    },
    messageSent: {
        backgroundColor: '#2196F3',
        color: 'white',
        marginLeft: 'auto',
        textAlign: 'right',
    },
    messageReceived: {
        backgroundColor: '#e0e0e0',
        color: '#000',
        marginRight: 'auto',
    },
    chatInput: {
        padding: 16,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    sectionTitle: {
        color: '#FFC107',
        fontWeight: 'bold',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    yellowButton: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px',
        fontSize: '18px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        width: '100%',
        marginTop: '8px',
        '&:hover': {
            backgroundColor: '#FFB300',
        },
    },
    composeCard: {
        padding: 8,
        boxShadow: 'none',
    },
    clickableBadge: {
        cursor: 'pointer',
        display: 'inline-flex',
        borderRadius: 8,
        padding: 4,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(15, 59, 98, 0.08)',
        },
    },
    clickableBadgeLight: {
        cursor: 'pointer',
        display: 'inline-flex',
        borderRadius: 8,
        padding: 4,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
    },
    unreadPanelHint: {
        fontSize: '0.78rem',
        color: '#64748b',
        marginTop: 4,
    },
}));

export default function NoteList() {
    const classes = useStyles();
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem("userDetail") || "{}"), []);

    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [notesList, setNotesList] = useState([]);
    const [chatOpen, setChatOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [unreadCounts, setUnreadCounts] = useState({}); // { noteId: count }
    const [studentUnreadCounts, setStudentUnreadCounts] = useState({}); // { studentId: totalCount }
    const totalSchoolChatUnread = useMemo(() => {
        const byStudent = Object.values(studentUnreadCounts).reduce((a, v) => a + (Number(v) || 0), 0);
        const byNote = Object.values(unreadCounts).reduce((a, v) => a + (Number(v) || 0), 0);
        return Math.max(byStudent, byNote);
    }, [studentUnreadCounts, unreadCounts]);
    const [classesList, setClassesList] = useState([]);
    const [sectionsList, setSectionsList] = useState([]);
    const [filterClassId, setFilterClassId] = useState("");
    const [filterSectionId, setFilterSectionId] = useState("");
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeStudent, setComposeStudent] = useState(null);
    const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
    const socketInitializedRef = useRef(false);
    const chatMessagesEndRef = useRef(null);
    const groupedDataMapRef = useRef(new Map()); // Store grouped notes data
    const groupedStudentDetailsRef = useRef(new Map()); // studentId -> student summary
    const chatOpenRef = useRef(false);
    const selectedNoteRef = useRef(null);
    /** noteId -> studentId. Used to attribute unread bumps to the right student row. */
    const noteToStudentRef = useRef(new Map());
    const [showUnreadStudentsOnly, setShowUnreadStudentsOnly] = useState(false);

    useEffect(() => {
        chatOpenRef.current = chatOpen;
    }, [chatOpen]);
    useEffect(() => {
        selectedNoteRef.current = selectedNote;
    }, [selectedNote]);

    const canComposeForStudent = () => {
        const r = String(userDetails?.role || "").toUpperCase();
        return r === "SCHOOLE" || r === "TEACHER" || r === "SUPERADMIN";
    };

    const openComposeForStudent = (student) => {
        if (!student?._id || !canComposeForStudent()) return;
        setComposeStudent(student);
        setSelectedStudent(student);
        setComposeOpen(true);
    };

    const mergeCreatedNoteIntoCache = (studentId, createdNote) => {
        if (!studentId || !createdNote?._id) return;
        const sid = String(studentId);
        const prev = groupedDataMapRef.current.get(sid) || [];
        const withoutDup = prev.filter((n) => String(n._id) !== String(createdNote._id));
        const next = [createdNote, ...withoutDup];
        groupedDataMapRef.current.set(sid, next);
        // Keep the noteId -> studentId map current so unread bumps land on the right row.
        noteToStudentRef.current.set(String(createdNote._id), sid);
        if (selectedStudent?._id && String(selectedStudent._id) === sid) {
            setNotesList(next);
        }
    };

    const composeFormik = useFormik({
        initialValues: { noteType: '', note: '' },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            noteType: Yup.string().required('Please select Note Type'),
            note: Yup.string(),
        }),
        onSubmit: async (values, { resetForm }) => {
            const student = composeStudent || selectedStudent;
            if (!student?._id) return;
            try {
                const classRef = student.classId;
                const classId = typeof classRef === 'object' ? classRef?._id : classRef;
                const schoolRef = student.schooleId || userDetails?.schooleId || userDetails?.schoolId;
                const schooleId = typeof schoolRef === 'object' ? schoolRef?._id : schoolRef;

                const body = {
                    messageKind: 'NOTE',
                    noteType: values.noteType,
                    note: values.note || '',
                    studentId: student._id,
                };
                if (classId) body.classId = classId;
                if (schooleId) body.schooleId = schooleId;

                const res = await NoteService.createNote(body);
                const createdNote = res?.data || res;
                mergeCreatedNoteIntoCache(student._id, createdNote);
                resetForm();
                setComposeOpen(false);
                setComposeStudent(null);
                setToast({ open: true, type: 'success', message: 'Message sent successfully.' });
            } catch (err) {
                setToast({
                    open: true,
                    type: 'error',
                    message: err.response?.data?.message || err?.message || 'Error sending message',
                });
            }
        },
    });

    useEffect(() => {
        loadStudents();

        // Initialize socket connection
        if (!socketInitializedRef.current) {
            SocketService.initializeSocket();
            socketInitializedRef.current = true;
        }

        // Seed unread counts ONCE from a single aggregated endpoint.
        NoteService.getUnreadSummary()
            .then((res) => {
                const data = res?.data || res || {};
                setUnreadCounts(data.byNoteId || {});
                setStudentUnreadCounts(data.byStudentId || {});
            })
            .catch((err) => console.error('Error fetching unread summary:', err));

        // Real-time bump per receiving message
        const offUpdate = SocketService.onUnreadUpdate((payload) => {
            const noteId = payload?.noteId;
            if (!noteId) return;
            const inc = Number(payload.increment) || 1;

            // If I'm currently looking at this exact thread, advance my server read
            // marker instead of incrementing the badge — keeps cross-device counts honest.
            if (
                chatOpenRef.current &&
                selectedNoteRef.current &&
                String(selectedNoteRef.current._id) === String(noteId)
            ) {
                if (!SocketService.markNoteReadSocket(noteId)) {
                    NoteService.markNoteRead(noteId).catch(() => {});
                }
                return;
            }

            setUnreadCounts((prev) => ({
                ...prev,
                [noteId]: (Number(prev[noteId]) || 0) + inc,
            }));

            const studentId =
                payload?.studentId || noteToStudentRef.current.get(String(noteId));
            if (studentId) {
                const sid = String(studentId);
                setStudentUnreadCounts((prev) => ({
                    ...prev,
                    [sid]: (Number(prev[sid]) || 0) + inc,
                }));
            }
        });

        // Real-time clear when *I* opened that thread (also fires across my devices)
        const offCleared = SocketService.onUnreadCleared((payload) => {
            const noteId = payload?.noteId;
            if (!noteId) return;
            setUnreadCounts((prev) => {
                if (!(noteId in prev)) return prev;
                const dropped = Number(prev[noteId]) || 0;
                const next = { ...prev };
                delete next[noteId];
                // Decrement the matching student bucket by the same amount.
                const studentId = noteToStudentRef.current.get(String(noteId));
                if (studentId && dropped > 0) {
                    const sid = String(studentId);
                    setStudentUnreadCounts((sPrev) => {
                        const remaining = (Number(sPrev[sid]) || 0) - dropped;
                        const sNext = { ...sPrev };
                        if (remaining <= 0) delete sNext[sid];
                        else sNext[sid] = remaining;
                        return sNext;
                    });
                }
                return next;
            });
        });

        return () => {
            if (typeof offUpdate === 'function') offUpdate();
            if (typeof offCleared === 'function') offCleared();
            // Don't disconnect the global socket here — Header (and other pages) share it.
            socketInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedStudent) {
            loadNotesForStudent(selectedStudent._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStudent]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        const schooleId = userDetails?.schooleId || userDetails?.schoolId;
        if (!(role === "SCHOOLE" || role === "SUPERADMIN") || !schooleId) return;
        AddClassService.getAllAddClass(schooleId, "SCHOOLE")
            .then((res) => setClassesList(Array.isArray(res) ? res : res?.data || []))
            .catch(() => setClassesList([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        const schooleId = userDetails?.schooleId || userDetails?.schoolId;
        if (!(role === "SCHOOLE" || role === "SUPERADMIN") || !schooleId) return;
        if (!filterClassId) {
            setSectionsList([]);
            setFilterSectionId("");
            return;
        }
        SectionService.findSectionList({ schooleId, classId: filterClassId, status: true })
            .then((res) => {
                const data = res?.data ?? res;
                setSectionsList(Array.isArray(data) ? data : []);
            })
            .catch(() => setSectionsList([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClassId]);

    const loadStudents = async () => {
        try {
            const schooleId = userDetails?.schooleId || userDetails?.schoolId;
            if (!schooleId) return;

            const role = String(userDetails?.role || "").toUpperCase();

            const groupedData = (await NoteService.getNotesGroupedByStudent()) || [];

            const groupedDataMap = new Map();
            const noteToStudent = new Map();
            groupedData.forEach((group) => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                if (studentId) {
                    const sid = String(studentId);
                    const notes = group.studentNotes || [];
                    const firstNote = notes[0] || null;
                    groupedDataMap.set(sid, notes);
                    groupedStudentDetailsRef.current.set(sid, {
                        _id: studentId,
                        firstName: group.studentDetails.firstName,
                        lastName: group.studentDetails.lastName,
                        classId: group.studentDetails.classId || firstNote?.classId || null,
                        sectionId: firstNote?.sectionId || null,
                    });
                    notes.forEach((n) => {
                        if (n?._id) noteToStudent.set(String(n._id), sid);
                    });
                }
            });
            groupedDataMapRef.current = groupedDataMap;
            noteToStudentRef.current = noteToStudent;

            if (role === "SCHOOLE" || role === "SUPERADMIN") {
                try {
                    const allStudentsRes = await StudentService.findStudentList({ schooleId });
                    const allStudents = allStudentsRes?.data || allStudentsRes || [];
                    const list = Array.isArray(allStudents) ? allStudents : [];
                    setStudentsList(list);
                    if (list.length > 0 && !selectedStudent && !showUnreadStudentsOnly) {
                        setSelectedStudent(list[0]);
                    }
                } catch (e) {
                    setStudentsList([]);
                }
                return;
            }

            if (groupedData.length === 0) {
                setStudentsList([]);
                return;
            }

            const studentsWithMessages = groupedData.map((group) => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                const firstNote = group.studentNotes && group.studentNotes.length > 0 ? group.studentNotes[0] : null;
                return {
                    _id: studentId,
                    firstName: group.studentDetails.firstName,
                    lastName: group.studentDetails.lastName,
                    classId: firstNote?.classId || null,
                    notes: group.studentNotes || [],
                };
            });

            setStudentsList(studentsWithMessages);
            if (studentsWithMessages.length > 0 && !selectedStudent && !showUnreadStudentsOnly) {
                setSelectedStudent(studentsWithMessages[0]);
            }
        } catch (err) {
            console.error('Error fetching students:', err);
            setStudentsList([]);
        }
    };

    const getStudentClassId = (student) => {
        const c = student?.classId;
        if (!c) return "";
        return typeof c === "object" ? String(c._id || "") : String(c);
    };

    const getStudentSectionId = (student) => {
        const s = student?.sectionId;
        if (!s) return "";
        return typeof s === "object" ? String(s._id || "") : String(s);
    };

    const getStudentUnreadCount = (studentId) => {
        const sid = String(studentId);
        const direct = Number(studentUnreadCounts[sid]) || 0;
        if (direct > 0) return direct;

        let fromNotes = 0;
        Object.entries(unreadCounts).forEach(([noteId, count]) => {
            if (noteToStudentRef.current.get(String(noteId)) === sid) {
                fromNotes += Number(count) || 0;
            }
        });
        return fromNotes;
    };

    const resolveStudentRecord = (studentId) => {
        const sid = String(studentId);
        const fromList = (studentsList || []).find((item) => String(item?._id) === sid);
        if (fromList) return fromList;

        const fromGrouped = groupedStudentDetailsRef.current.get(sid);
        if (fromGrouped) return fromGrouped;

        return { _id: studentId, firstName: "Student", lastName: "" };
    };

    const unreadStudents = useMemo(() => {
        const studentIds = new Set();

        Object.entries(studentUnreadCounts).forEach(([studentId, count]) => {
            if (Number(count) > 0) studentIds.add(String(studentId));
        });

        Object.entries(unreadCounts).forEach(([noteId, count]) => {
            if (Number(count) <= 0) return;
            const studentId = noteToStudentRef.current.get(String(noteId));
            if (studentId) studentIds.add(String(studentId));
        });

        return Array.from(studentIds)
            .map((studentId) => ({
                student: resolveStudentRecord(studentId),
                unreadCount: getStudentUnreadCount(studentId),
            }))
            .filter((entry) => entry.unreadCount > 0)
            .sort((a, b) => b.unreadCount - a.unreadCount)
            .map((entry) => entry.student);
        // noteToStudentRef is a ref; unreadCounts/studentUnreadCounts drive recomputation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentsList, studentUnreadCounts, unreadCounts]);

    const unreadStudentCount = unreadStudents.length;

    const handleShowUnreadStudents = () => {
        if (totalSchoolChatUnread <= 0 || unreadStudentCount <= 0) return;
        setShowUnreadStudentsOnly(true);
        setFilterClassId("");
        setFilterSectionId("");
        setSelectedStudent(unreadStudents[0]);
    };

    const renderUnreadBadge = (iconColor, light = false) => (
        <Box
            component="span"
            className={light ? classes.clickableBadgeLight : classes.clickableBadge}
            onClick={handleShowUnreadStudents}
            role="button"
            tabIndex={0}
            aria-label="View students with unseen messages"
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleShowUnreadStudents();
                }
            }}
        >
            <Badge
                badgeContent={totalSchoolChatUnread > 0 ? totalSchoolChatUnread : 0}
                color="error"
                overlap="rectangular"
                invisible={totalSchoolChatUnread <= 0}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <ForumIcon style={{ color: iconColor, fontSize: light ? 30 : 26 }} />
            </Badge>
        </Box>
    );

    /** School admin: show student list only after class is chosen, and section when class has sections */
    const adminStudentListVisible = useMemo(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return true;
        if (!filterClassId) return false;
        if (sectionsList.length > 0 && !filterSectionId) return false;
        return true;
    }, [userDetails, filterClassId, filterSectionId, sectionsList.length]);

    const filteredStudents = useMemo(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if ((role === "SCHOOLE" || role === "SUPERADMIN") && !adminStudentListVisible) {
            return [];
        }
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return studentsList;
        return (studentsList || []).filter((st) => {
            if (filterClassId && getStudentClassId(st) !== String(filterClassId)) return false;
            if (filterSectionId && getStudentSectionId(st) !== String(filterSectionId)) return false;
            return true;
        });
    }, [studentsList, userDetails, filterClassId, filterSectionId, adminStudentListVisible]);

    const displayStudents = showUnreadStudentsOnly ? unreadStudents : filteredStudents;

    useEffect(() => {
        if (!showUnreadStudentsOnly || unreadStudents.length === 0) return;
        const currentId = selectedStudent?._id ? String(selectedStudent._id) : null;
        const stillInUnreadList = currentId
            ? unreadStudents.some((student) => String(student?._id) === currentId)
            : false;
        if (!stillInUnreadList) {
            setSelectedStudent(unreadStudents[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUnreadStudentsOnly, unreadStudents]);

    useEffect(() => {
        if (showUnreadStudentsOnly && unreadStudentCount === 0 && totalSchoolChatUnread === 0) {
            setShowUnreadStudentsOnly(false);
        }
    }, [showUnreadStudentsOnly, unreadStudentCount, totalSchoolChatUnread]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return;
        if (showUnreadStudentsOnly) return;

        if (!filteredStudents || filteredStudents.length === 0) {
            setSelectedStudent(null);
            setNotesList([]);
            return;
        }

        const currentId = selectedStudent?._id ? String(selectedStudent._id) : null;
        const stillExists = currentId
            ? filteredStudents.some((s) => String(s?._id) === currentId)
            : false;

        if (!stillExists) {
            setSelectedStudent(filteredStudents[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClassId, filterSectionId, filteredStudents, showUnreadStudentsOnly]);

    const loadNotesForStudent = async (studentId) => {
        try {
            if (!studentId) return;

            const sid = String(studentId);
            // Use cached grouped data if available (from loadStudents)
            if (groupedDataMapRef.current && groupedDataMapRef.current.has(sid)) {
                const notes = groupedDataMapRef.current.get(sid);
                setNotesList(notes);
                notes.forEach((n) => {
                    if (n?._id) noteToStudentRef.current.set(String(n._id), sid);
                });
                return;
            }

            // Fallback: fetch from API if cached data not available
            const classId = userDetails?.role === 'TEACHER' ? userDetails?.classId : null;
            const res = await NoteService.getAllNotes(classId);
            const filtered = (res || []).filter((note) => {
                const noteStudentId = note?.studentId?._id || note?.studentId;
                return String(noteStudentId) === sid;
            });
            setNotesList(filtered);
            filtered.forEach((n) => {
                if (n?._id) noteToStudentRef.current.set(String(n._id), sid);
            });
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    };

    const loadChatMessages = async (noteId) => {
        try {
            if (!noteId) return;
            const res = await NoteService.getChatMessagesByNoteId(noteId);
            setChatMessages(res || []);
        } catch (err) {
            console.error('Error fetching chat messages:', err);
        }
    };

    const persistNoteRead = async (noteId) => {
        try {
            const sentViaSocket = SocketService.markNoteReadSocket(noteId);
            if (!sentViaSocket) {
                await NoteService.markNoteRead(noteId);
            }
        } catch (err) {
            console.error('Error marking note as read:', err);
        }
    };

    const markNotesAsRead = async (noteIds, studentId) => {
        const ids = (noteIds || []).map((id) => String(id)).filter(Boolean);
        if (ids.length === 0) return;

        const sid = studentId ? String(studentId) : null;
        let totalDropped = 0;

        setUnreadCounts((prev) => {
            const next = { ...prev };
            ids.forEach((noteId) => {
                totalDropped += Number(next[noteId]) || 0;
                delete next[noteId];
            });
            return next;
        });

        if (sid && totalDropped > 0) {
            setStudentUnreadCounts((prev) => {
                const remaining = (Number(prev[sid]) || 0) - totalDropped;
                const next = { ...prev };
                if (remaining <= 0) delete next[sid];
                else next[sid] = remaining;
                return next;
            });
        }

        await Promise.all(ids.map((noteId) => persistNoteRead(noteId)));
    };

    const getUnreadNoteIdsForStudent = (studentId, notes = []) => {
        const sid = String(studentId);
        const unreadNoteIds = new Set();

        Object.entries(unreadCounts).forEach(([noteId, count]) => {
            if (Number(count) > 0 && noteToStudentRef.current.get(String(noteId)) === sid) {
                unreadNoteIds.add(String(noteId));
            }
        });

        (notes || []).forEach((note) => {
            if (note?._id && (Number(unreadCounts[note._id]) || 0) > 0) {
                unreadNoteIds.add(String(note._id));
            }
        });

        return Array.from(unreadNoteIds);
    };

    const handleSelectStudent = (student) => {
        setSelectedStudent(student);
        setNotesList([]);
    };

    useEffect(() => {
        if (!selectedStudent?._id || !Array.isArray(notesList) || notesList.length === 0) return;

        const unreadNoteIds = getUnreadNoteIdsForStudent(selectedStudent._id, notesList);
        if (unreadNoteIds.length === 0) return;

        markNotesAsRead(unreadNoteIds, selectedStudent._id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStudent, notesList, unreadCounts]);

    const handleOpenChat = async (note) => {
        setSelectedNote(note);
        setChatOpen(true);
        if (!note?._id) return;

        // Load initial chat messages
        await loadChatMessages(note._id);

        // Join socket room for real-time updates while the dialog is open
        SocketService.joinNoteChat(note._id, {
            onJoined: (data) => {
                console.log('Joined room:', data);
            },
            onMessage: (messageData) => {
                setChatMessages((prev) => {
                    const exists = prev.some((msg) => msg._id === messageData._id);
                    if (exists) return prev;
                    return [...prev, messageData];
                });
                setTimeout(() => {
                    if (chatMessagesEndRef.current) {
                        chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            },
            onError: (error) => {
                console.error('Socket error:', error);
            },
        });

        // Optimistic clear for this thread (student-wide clear may already have run on view)
        if ((Number(unreadCounts[note._id]) || 0) > 0) {
            await markNotesAsRead([note._id], note?.studentId?._id || note?.studentId || selectedStudent?._id);
        }
    };

    const handleCloseChat = () => {
        // Leave socket room
        if (selectedNote?._id) {
            SocketService.leaveNoteChat(selectedNote._id);
        }
        setChatOpen(false);
        setSelectedNote(null);
        setChatMessages([]);
        setChatInput('');
    };

    const handleSendMessage = async () => {
        try {
            const text = (chatInput || '').trim();
            if (!text) return;
            if (!selectedNote?._id) return;

            const studentId = selectedNote?.studentId?._id || selectedNote?.studentId || selectedStudent?._id;
            if (!studentId) return;

            setChatInput('');

            // Try sending via socket first (real-time)
            if (SocketService.isConnected()) {
                SocketService.sendMessage(selectedNote._id, text, {
                    onSuccess: () => {
                        // receive-message event will append the message; nothing else to do.
                    },
                    onError: (error) => {
                        console.error('Socket send error, falling back to REST:', error);
                        sendViaRestAPI(studentId, text);
                    },
                });
            } else {
                await sendViaRestAPI(studentId, text);
            }
        } catch (err) {
            console.error('Error sending message:', err);
        }
    };

    const sendViaRestAPI = async (studentId, text) => {
        try {
            await NoteService.sendChatMessage({
                studentId,
                note: text,
                parentNoteId: selectedNote._id,
            });
            await loadChatMessages(selectedNote._id);
            setTimeout(() => {
                if (chatMessagesEndRef.current) {
                    chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } catch (err) {
            console.error('Error sending via REST API:', err);
        }
    };

    const getStudentName = (student) => {
        if (!student) return '-';
        const first = student.firstName || '';
        const last = student.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getStudentNameFromNote = (note) => {
        const s = note?.studentId;
        if (!s) return '-';
        const first = s.firstName || '';
        const last = s.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getClassName = (student) => {
        return student?.classId?.className || '-';
    };

    const getClassNameFromNote = (note) => {
        return note?.studentId?.classId?.className || note?.classId?.className || '-';
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeShort = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const isMine = (msg) => {
        const me = userDetails?._id;
        const sender = msg?.createdBy?._id;
        return Boolean(me && sender && String(me) === String(sender));
    };

    return (
        <>
            <PageTitle title="Notes List" />
            <div className={classes.yellowHeader}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
                    <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
                        Messages from Parents
                    </Typography>
                    {renderUnreadBadge("white", true)}
                </Box>
            </div>

            <Widget>
                <Box className={classes.layout}>
                    {/* Left Panel - Students List */}
                    <Paper className={classes.studentsPanel}>
                        <Box p={2} className={classes.studentsPanelHeader}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
                                <Box>
                                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                        {showUnreadStudentsOnly ? "Unread Students" : "Students"}
                                    </Typography>
                                    {showUnreadStudentsOnly && (
                                        <Typography className={classes.unreadPanelHint}>
                                            {unreadStudentCount} student{unreadStudentCount === 1 ? "" : "s"} with unseen messages
                                        </Typography>
                                    )}
                                </Box>
                                {renderUnreadBadge("#0f3b62")}
                            </Box>
                            {showUnreadStudentsOnly && (
                                <Box mt={1.5}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setShowUnreadStudentsOnly(false)}
                                        style={{ textTransform: "none" }}
                                    >
                                        Show all students
                                    </Button>
                                </Box>
                            )}
                            {["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) &&
                                !showUnreadStudentsOnly && (
                                <Box mt={2} display="flex" flexDirection="column" gridGap={12}>
                                    <FormControl variant="outlined" fullWidth size="small">
                                        <InputLabel id="note-list-filter-class">Class</InputLabel>
                                        <Select
                                            labelId="note-list-filter-class"
                                            value={filterClassId}
                                            onChange={(e) => {
                                                setFilterClassId(e.target.value);
                                                setFilterSectionId("");
                                                setSectionsList([]);
                                                setShowUnreadStudentsOnly(false);
                                            }}
                                            label="Class"
                                        >
                                            <MenuItem value="">
                                                <em>All classes</em>
                                            </MenuItem>
                                            {classesList.map((c) => (
                                                <MenuItem key={c._id} value={c._id}>
                                                    {c.className}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {filterClassId && sectionsList.length > 0 && (
                                        <FormControl variant="outlined" fullWidth size="small">
                                            <InputLabel id="note-list-filter-section">Section</InputLabel>
                                            <Select
                                                labelId="note-list-filter-section"
                                                value={filterSectionId}
                                                onChange={(e) => {
                                                    setFilterSectionId(e.target.value);
                                                    setShowUnreadStudentsOnly(false);
                                                }}
                                                label="Section"
                                            >
                                                <MenuItem value="">
                                                    <em>All sections</em>
                                                </MenuItem>
                                                {sectionsList.map((s) => (
                                                    <MenuItem key={s._id} value={s._id}>
                                                        {s.sectionName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            )}
                        </Box>
                        {["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) &&
                            !showUnreadStudentsOnly &&
                            !adminStudentListVisible && (
                                <Box px={2} pb={2}>
                                    <Typography variant="body2" style={{ color: "#777" }}>
                                        {!filterClassId
                                            ? "Select a class to view students."
                                            : "Select a section to view students."}
                                    </Typography>
                                </Box>
                            )}
                        <Divider
                            style={{
                                flexShrink: 0,
                                display:
                                    ["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) &&
                                    !showUnreadStudentsOnly &&
                                    !adminStudentListVisible
                                        ? "none"
                                        : undefined,
                            }}
                        />
                        <Box
                            className={classes.studentsListScroll}
                            style={{
                                display:
                                    ["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) &&
                                    !showUnreadStudentsOnly &&
                                    !adminStudentListVisible
                                        ? "none"
                                        : undefined,
                            }}
                        >
                            <List dense component="div" disablePadding>
                            {displayStudents.length === 0 ? (
                                <Box p={2}>
                                    <Typography variant="body2" style={{ color: '#777' }}>
                                        {showUnreadStudentsOnly
                                            ? "No unseen messages right now."
                                            : !["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) ||
                                              adminStudentListVisible
                                                ? studentsList.length === 0
                                                    ? "No students found."
                                                    : "No students match the selected class or section."
                                                : ""}
                                    </Typography>
                                </Box>
                            ) : (
                                displayStudents.map((student) => {
                                    const selected = selectedStudent?._id && student._id
                                        ? String(selectedStudent._id) === String(student._id)
                                        : false;
                                    const studentIdStr = student._id ? String(student._id) : String(student);
                                    const unreadCount = getStudentUnreadCount(studentIdStr);
                                    const hasUnread = unreadCount > 0;
                                    return (
                                        <React.Fragment key={student._id}>
                                            <ListItem
                                                button
                                                selected={selected}
                                                onClick={() => handleSelectStudent(student)}
                                                className={classes.studentRow}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            className={classes.studentRowName}
                                                            style={{ fontWeight: hasUnread || selected ? 700 : 400 }}
                                                        >
                                                            {getStudentName(student)}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography className={classes.studentRowMeta}>
                                                            {getClassName(student)}
                                                            {hasUnread ? ` · ${unreadCount} unseen` : ""}
                                                        </Typography>
                                                    }
                                                />
                                                {hasUnread && (
                                                    <span className={classes.studentRowDot} aria-label={`${unreadCount} unread`}>
                                                        {unreadCount > 99 ? '99+' : unreadCount}
                                                    </span>
                                                )}
                                                {canComposeForStudent() && (
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            aria-label="Write a message"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openComposeForStudent(student);
                                                            }}
                                                        >
                                                            <MessageIcon fontSize="small" />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                )}
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    );
                                })
                            )}
                            </List>
                        </Box>
                    </Paper>

                    {/* Right Panel - Notes List */}
                    <Paper className={classes.notesPanel}>
                        <Box
                            p={2}
                            style={{
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                {selectedStudent ? `Notes from ${getStudentName(selectedStudent)}` : 'Select a student'}
                            </Typography>
                            {selectedStudent && (
                                <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
                                    <Chip
                                        label={`Notes: ${Array.isArray(notesList) ? notesList.length : 0}`}
                                        size="small"
                                        style={{ fontWeight: 700 }}
                                    />
                                    <Badge
                                        badgeContent={
                                            getStudentUnreadCount(selectedStudent?._id) > 0
                                                ? getStudentUnreadCount(selectedStudent._id)
                                                : 0
                                        }
                                        color="error"
                                        overlap="rectangular"
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    >
                                        <NotificationsIcon style={{ color: '#0F3B62' }} />
                                    </Badge>
                                    {canComposeForStudent() && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<MessageIcon />}
                                            onClick={() => openComposeForStudent(selectedStudent)}
                                            style={{ backgroundColor: '#0F3B62', color: '#fff' }}
                                        >
                                            Write a message
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.notesContainer}>
                            {!selectedStudent ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        Please select a student from the left panel.
                                    </Typography>
                                </Box>
                            ) : notesList.length === 0 ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        No messages from this student yet.
                                    </Typography>
                                </Box>
                            ) : (
                                notesList.map((note) => {
                                    const unreadCount = unreadCounts[note._id] || 0;
                                    return (
                                        <Box
                                            key={note._id}
                                            className={`${classes.noteBubble} ${unreadCount > 0 ? classes.noteBubbleUnread : ''}`}
                                            onClick={() => handleOpenChat(note)}
                                            style={{ width: '100%' }}
                                        >
                                            <Box className={classes.noteHeader}>
                                                {note.noteType && (
                                                    <Chip
                                                        label="Submitted"
                                                        size="small"
                                                        style={{
                                                            backgroundColor: '#4caf50',
                                                            color: 'white',
                                                            fontSize: '0.65rem',
                                                            height: '18px',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {note.noteType && (
                                                <Typography className={classes.noteType}>
                                                    📋 {note.noteType}
                                                </Typography>
                                            )}
                                            {note.note && (
                                                <Typography className={classes.noteText}>
                                                    {note.note}
                                                </Typography>
                                            )}
                                            <Box className={classes.noteRowMeta}>
                                                <Typography className={classes.noteTime}>
                                                    {formatTime(note.createdAt)}
                                                </Typography>
                                                {unreadCount > 0 && (
                                                    <span className={classes.unreadDot} aria-label={`${unreadCount} unread`}>
                                                        {unreadCount > 99 ? '99+' : unreadCount}
                                                    </span>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Widget>

            {/* Chat Dialog */}
            <Dialog
                open={chatOpen}
                onClose={handleCloseChat}
                className={classes.chatDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.chatHeader}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
                        {selectedNote ? `${getStudentNameFromNote(selectedNote)} • ${getClassNameFromNote(selectedNote)}` : 'Chat'}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseChat}
                        style={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.chatMessages}>
                    {chatMessages.length === 0 ? (
                        <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            <Typography variant="body2">
                                No messages yet. Start a conversation!
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {chatMessages.map((m, idx) => {
                                const isFormNote = m.noteType && (m.messageKind !== 'CHAT' || !m.messageKind);
                                return (
                                    <Box
                                        key={m._id || idx}
                                        className={`${classes.chatMessageBubble} ${
                                            isMine(m) ? classes.messageSent : classes.messageReceived
                                        }`}
                                        style={isFormNote ? { backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404' } : {}}
                                    >
                                        {isFormNote && (
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <Typography variant="caption" style={{ fontWeight: 700, fontSize: '0.7rem', opacity: 0.9 }}>
                                                    📋 {m.noteType}
                                                </Typography>
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#4caf50',
                                                        color: 'white',
                                                        fontSize: '0.65rem',
                                                        height: '18px',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Typography variant="body2">{m.note || '-'}</Typography>
                                        <Typography variant="caption" style={{ display: 'block', marginTop: 4, opacity: 0.75, fontSize: '0.7rem' }}>
                                            {formatTimeShort(m.createdAt)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            <div ref={chatMessagesEndRef} />
                        </>
                    )}
                </DialogContent>
                <DialogActions className={classes.chatInput}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type a message..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && chatInput.trim()) {
                                handleSendMessage();
                            }
                        }}
                        size="small"
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!chatInput.trim()}
                    >
                        <SendIcon />
                    </IconButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={composeOpen}
                onClose={() => {
                    setComposeOpen(false);
                    setComposeStudent(null);
                    composeFormik.resetForm();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ fontWeight: 800 }}>
                    {composeStudent || selectedStudent
                        ? `Write a message — ${getStudentName(composeStudent || selectedStudent)}`
                        : 'Write a message'}
                </DialogTitle>
                <DialogContent>
                    <Card className={classes.composeCard}>
                        <CardContent>
                            <form id="note-list-compose-form" onSubmit={composeFormik.handleSubmit}>
                                <FormControl component="fieldset" fullWidth style={{ marginBottom: 24 }}>
                                    <FormLabel component="legend" className={classes.sectionTitle}>
                                        <span style={{ fontSize: 20 }}>📋</span>
                                        Note Type*
                                    </FormLabel>
                                    <RadioGroup
                                        name="noteType"
                                        value={composeFormik.values.noteType}
                                        onChange={composeFormik.handleChange}
                                        style={{ marginTop: 8 }}
                                    >
                                        <FormControlLabel
                                            value="Request for in person meeting with teacher"
                                            control={<Radio color="primary" />}
                                            label="Request for in person meeting with teacher"
                                        />
                                        <FormControlLabel
                                            value="Request for a call post school hours"
                                            control={<Radio color="primary" />}
                                            label="Request for a call post school hours"
                                        />
                                        <FormControlLabel
                                            value="Other"
                                            control={<Radio color="primary" />}
                                            label="Other"
                                        />
                                    </RadioGroup>
                                    {composeFormik.touched.noteType && composeFormik.errors.noteType && (
                                        <Typography variant="caption" style={{ color: '#d32f2f', marginTop: 6, display: 'block' }}>
                                            {composeFormik.errors.noteType}
                                        </Typography>
                                    )}
                                </FormControl>
                                <Box style={{ marginBottom: 16 }}>
                                    <FormLabel className={classes.sectionTitle} style={{ marginBottom: 8 }}>
                                        Message (Optional)
                                    </FormLabel>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        name="note"
                                        value={composeFormik.values.note}
                                        onChange={composeFormik.handleChange}
                                        placeholder="Enter your message here..."
                                        variant="outlined"
                                        style={{ marginTop: 8 }}
                                    />
                                </Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.yellowButton}
                                    disabled={!composeFormik.values.noteType || composeFormik.isSubmitting}
                                >
                                    SEND
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setComposeOpen(false);
                            setComposeStudent(null);
                            composeFormik.resetForm();
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    style={{
                        backgroundColor: toast.type === 'success' ? '#2e7d32' : '#d32f2f',
                        color: '#fff',
                        fontWeight: 600,
                    }}
                    message={toast.message}
                />
            </Snackbar>
        </>
    );
}
